<template>
  <div class="goods-form-page">
    <!-- 顶部操作栏 -->
    <header class="form-header">
      <div class="header-left">
        <button class="back-btn" @click="goBack">← 返回</button>
        <h1 class="page-title">{{ isEdit ? '编辑商品' : '新增商品' }}</h1>
        <span class="goods-id" v-if="goodsId">商品 ID：{{ goodsId }}</span>
      </div>
      <div class="header-right">
        <button class="ghost-btn" @click="goBack">取消</button>
        <button class="save-btn" :disabled="saving || !adminAuthed" @click="submitGoods">
          {{ saving ? '保存中...' : '保存商品' }}
        </button>
      </div>
    </header>

    <div class="form-body">
      <!-- 左侧主表单 -->
      <div class="form-main">
        <!-- ===== 基础信息 ===== -->
        <section class="card">
          <div class="card-title">
            <span>📦 基础信息</span>
            <span class="card-sub">带 * 为必填项</span>
          </div>
          <div class="grid">
            <div class="f full">
              <label>商品名称 <span class="required">*</span></label>
              <input v-model.trim="form.goods_name" maxlength="120" placeholder="请输入商品名称">
            </div>
            <div class="f full">
              <label>副标题</label>
              <input v-model.trim="form.goods_subtitle" maxlength="120" placeholder="一句话卖点（选填）">
            </div>
            <div class="f">
              <label>分类 ID <span class="required">*</span></label>
              <input v-model="form.category_id" type="number" min="1" placeholder="请输入分类 ID">
            </div>
            <div class="f">
              <label>库存 <span class="required">*</span></label>
              <input v-model="form.goods_stock" type="number" min="0" placeholder="请输入库存">
            </div>
            <div class="f">
              <label>最低价 <span class="required">*</span></label>
              <input v-model="form.goods_price_min" type="number" min="0" step="0.01" placeholder="如：9.90">
            </div>
            <div class="f">
              <label>最高价</label>
              <input v-model="form.goods_price_max" type="number" min="0" step="0.01" placeholder="不填则与最低价一致">
            </div>
            <div class="f full">
              <label>商品详情</label>
              <textarea v-model="form.goods_desc" rows="5" placeholder="支持 HTML，如：&lt;p&gt;商品介绍&lt;/p&gt;"></textarea>
            </div>
          </div>
          <p class="card-tip">填写规格（SKU）后，商品价格区间与总库存将按规格自动计算。</p>
        </section>

        <!-- ===== 商品图片 ===== -->
        <section class="card">
          <div class="card-title"><span>🖼️ 商品主图</span></div>
          <div class="image-row">
            <div class="image-preview">
              <img v-if="form.goods_image" :src="form.goods_image" alt="">
              <span v-else class="placeholder">暂无图片</span>
            </div>
            <div class="image-input">
              <input v-model.trim="form.goods_image" placeholder="图片 URL，或从图片空间选择">
              <div class="image-actions">
                <button type="button" class="picker-btn" @click="openImagePicker('goods_image')">从图片空间选</button>
                <button type="button" class="picker-btn ghost" v-if="form.goods_image" @click="form.goods_image = ''">清除</button>
              </div>
            </div>
          </div>
        </section>

        <!-- ===== 商品规格（SKU） ===== -->
        <section class="card">
          <div class="card-title">
            <span>🧩 商品规格（SKU）</span>
            <button type="button" class="mini-btn" @click="addSku">＋ 添加规格</button>
          </div>
          <p class="card-tip">可配置多个规格组合（如「红色 / 大号」）。填写规格后，用户购买时需选择规格，价格与库存按规格计算。</p>

          <div class="sku-row" v-for="(sku, index) in form.skus" :key="index">
            <div class="sku-head">
              <span class="sku-index">规格 {{ index + 1 }}</span>
              <button type="button" class="del-btn" @click="removeSku(index)">删除</button>
            </div>
            <div class="grid">
              <div class="f">
                <label>规格组合</label>
                <input v-model.trim="sku.spec_name" placeholder="如：红色 / 大号">
              </div>
              <div class="f">
                <label>价格 <span class="required">*</span></label>
                <input v-model="sku.price" type="number" min="0" step="0.01" placeholder="规格价格">
              </div>
              <div class="f">
                <label>库存</label>
                <input v-model="sku.stock" type="number" min="0" placeholder="规格库存">
              </div>
              <div class="f full">
                <label>规格图片</label>
                <div class="inline-row">
                  <input v-model.trim="sku.image" placeholder="图片 URL">
                  <button type="button" class="picker-btn" @click="openImagePicker('sku_image', index)">选图</button>
                </div>
              </div>
            </div>
          </div>

          <div class="empty-tip" v-if="!form.skus.length">未添加规格：商品将按「最低价 / 库存」直接下单。</div>
        </section>

        <!-- ===== 自动发货（虚拟卡密） ===== -->
        <section class="card">
          <div class="card-title">
            <span>🎫 自动发货（虚拟卡密）</span>
            <span class="card-sub">按规格绑定卡密库</span>
          </div>
          <p class="card-tip">
            开启后，买家支付成功即自动从卡密库取卡发送给买家（只发送「卡密内容」与「使用说明」原文，分两条消息）；
            未单独配置的规格将使用「默认规格」配置。卡密库需在商品保存后才可管理。
          </p>

          <div class="auto-row" v-for="(cfg, index) in autoConfigs" :key="index">
            <div class="auto-head">
              <span class="auto-sku">{{ autoRowName(index) }}</span>
              <label class="switch-label">
                <input type="checkbox" :checked="cfg.enabled === 1" @change="cfg.enabled = cfg.enabled === 1 ? 0 : 1">
                启用自动发货
              </label>
            </div>

            <template v-if="cfg.enabled === 1">
              <label class="auto-opt">
                <input type="checkbox" :checked="cfg.update_order_status === 1" @change="cfg.update_order_status = cfg.update_order_status === 1 ? 0 : 1">
                发货后更新订单状态为「待收货」（不勾选则订单保持「待发货」）
              </label>
              <label class="auto-opt">
                <input type="checkbox" :checked="cfg.allow_repeat_card === 1" @change="cfg.allow_repeat_card = cfg.allow_repeat_card === 1 ? 0 : 1">
                重复卡密（同一张卡可重复出售；不勾选则每张卡只能使用一次）
              </label>
              <div class="f full">
                <label>使用说明（选填，将作为单独一条消息原文发送给买家）</label>
                <textarea v-model="cfg.usage_note" rows="2" placeholder="如：在 APP「我的-兑换中心」输入卡密即可完成充值"></textarea>
              </div>
              <div class="card-manage">
                <span class="card-stat">卡密库：<b>{{ cfg.card_available || 0 }}</b> 可用 / {{ cfg.card_total || 0 }} 总</span>
                <button type="button" class="picker-btn" @click="openCardModal(index)">管理卡密</button>
              </div>
            </template>
          </div>
        </section>
      </div>

      <!-- 右侧设置栏 -->
      <aside class="form-side">
        <section class="card">
          <div class="card-title"><span>⚙️ 上架与营销</span></div>

          <div class="switch-row">
            <span class="switch-name">上架销售</span>
            <div class="custom-switch" :class="{ active: form.is_on_sale }" @click="form.is_on_sale = !form.is_on_sale">
              <div class="switch-track"><div class="switch-thumb"></div></div>
            </div>
          </div>
          <p class="card-tip">关闭后商品在商城不可见（后台仍可管理）。</p>

          <div class="switch-row">
            <span class="switch-name">首页展示</span>
            <div class="custom-switch" :class="{ active: form.show_on_home }" @click="form.show_on_home = !form.show_on_home">
              <div class="switch-track"><div class="switch-thumb"></div></div>
            </div>
          </div>
          <p class="card-tip">开启后商品会出现在商城首页「猜你喜欢」。</p>
        </section>

        <section class="card tips">
          <div class="card-title"><span>💡 操作提示</span></div>
          <ul>
            <li>填写规格后，价格区间 / 总库存按规格自动计算。</li>
            <li>规格保存后会保留规格 ID，自动发货配置与卡密不会失效。</li>
            <li>自动发货卡密不足时会跳过发卡并记录日志，补卡后可手动发货。</li>
            <li>新增商品保存后，可在「商品管理」列表中继续编辑。</li>
          </ul>
        </section>
      </aside>
    </div>

    <!-- ===== 图片空间选择弹窗 ===== -->
    <div class="modal-overlay" v-if="imagePickerVisible" @click.self="closeImagePicker">
      <div class="modal-content image-picker-modal">
        <div class="modal-header">
          <span>从图片空间选择图片</span>
          <button class="modal-close" @click="closeImagePicker">✕</button>
        </div>
        <div class="modal-body">
          <div class="picker-toolbar">
            <input ref="pickerFileInput" type="file" accept="image/*" style="display:none" @change="handlePickerUpload">
            <button class="picker-btn" :disabled="imageUploading" @click="$refs.pickerFileInput.click()">
              {{ imageUploading ? '上传中...' : '📤 上传新图片' }}
            </button>
          </div>
          <div class="picker-grid" v-if="imageList.length > 0">
            <div class="picker-card" v-for="img in imageList" :key="img.id" @click="selectPickedImage(img)">
              <img :src="img.image_url" alt="">
              <span class="picker-name" :title="img.file_name">{{ img.file_name }}</span>
            </div>
          </div>
          <div v-else class="empty-tip">暂无图片，请先上传</div>
          <div class="pager" v-if="imageTotalPage > 1">
            <button class="page-btn" :disabled="imagePage <= 1" @click="loadImageLibrary(imagePage - 1)">上一页</button>
            <span class="page-num">{{ imagePage }} / {{ imageTotalPage }}</span>
            <button class="page-btn" :disabled="imagePage >= imageTotalPage" @click="loadImageLibrary(imagePage + 1)">下一页</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 卡密库管理弹窗（自动发货） ===== -->
    <CardLibraryModal
      :visible.sync="cardModalVisible"
      :goods-id="cardModalGoodsId"
      :sku-id="cardModalSkuId"
      :sku-name="cardModalSkuName"
      @changed="onCardsChanged"
    />

    <!-- ===== 管理员登录校验遮罩 ===== -->
    <AdminLogin
      v-if="!adminAuthed"
      :loading="loginLoading"
      :captcha-img="captchaImg"
      :captcha-key="captchaKey"
      @login="handleLogin"
      @refresh-captcha="loadCaptcha"
    />
  </div>
</template>

<script>
import AdminLogin from './LoginModal.vue'
import CardLibraryModal from './CardLibraryModal.vue'
import {
  getAdminGoodsDetailApi,
  addGoodsApi,
  editGoodsApi,
  saveAutoDeliveryApi,
  getGoodsCardsApi,
  uploadImageLibraryApi,
  getImageLibraryApi,
  getAdminCaptchaApi,
  adminLoginApi,
  adminCheckAuthApi
} from '@/api/admin'

/**
 * 商品新增 / 编辑页面（独立页面，路由 /admin/goods-form，?id= 表示编辑）
 *
 * 布局：
 *   顶部吸顶操作栏（返回 / 标题 / 保存）
 *   左侧主区：基础信息 → 商品主图 → 商品规格（SKU）→ 自动发货（按规格绑定卡密）
 *   右侧侧栏：上架与营销开关 + 操作提示（窄屏自动变为单列）
 */
export default {
  name: 'AdminGoodsForm',
  components: { AdminLogin, CardLibraryModal },
  data () {
    return {
      // ===== 管理员登录校验 =====
      adminAuthed: false,
      loginLoading: false,
      captchaImg: '',
      captchaKey: '',
      // ===== 商品表单 =====
      saving: false,
      isEdit: false,
      editId: null,
      form: {
        goods_name: '',
        goods_subtitle: '',
        category_id: '',
        goods_price_min: '',
        goods_price_max: '',
        goods_stock: '100',
        goods_image: '',
        goods_desc: '',
        is_on_sale: true,
        show_on_home: false,
        skus: []
      },
      // ===== 自动发货（虚拟卡密）=====
      autoDeliveryDefault: {
        sku_id: 0,
        enabled: 0,
        update_order_status: 1,
        allow_repeat_card: 0,
        usage_note: '',
        card_total: 0,
        card_available: 0
      },
      autoDeliveryList: [],
      cardModalVisible: false,
      cardModalGoodsId: 0,
      cardModalSkuId: 0,
      cardModalSkuName: '',
      // ===== 图片空间选择 =====
      imagePickerVisible: false,
      imagePickerTarget: null,
      imageList: [],
      imagePage: 1,
      imageTotalPage: 1,
      imageUploading: false
    }
  },
  computed: {
    // 当前商品 id（新增时为 0）
    goodsId () {
      return this.isEdit ? this.editId : 0
    },
    // 自动发货配置行：[0] 默认规格，其后与 form.skus 顺序一一对应
    autoConfigs () {
      const list = [this.autoDeliveryDefault]
      const skus = this.form.skus || []
      skus.forEach((sku, index) => {
        if (this.autoDeliveryList[index]) {
          list.push(this.autoDeliveryList[index])
        }
      })
      return list
    }
  },
  created () {
    const id = Number(this.$route.query.id) || 0
    this.isEdit = id > 0
    this.editId = id > 0 ? id : null
    this.loadCaptcha()
    this.checkAdminAuth()
  },
  methods: {
    // ==================== 管理员登录校验 ====================
    loadCaptcha () {
      getAdminCaptchaApi()
        .then((res) => {
          if (res.data) {
            this.captchaImg = res.data.img
            this.captchaKey = res.data.key
          }
        })
        .catch((e) => {
          console.error('获取验证码失败', e)
        })
    },
    checkAdminAuth () {
      const token = window.localStorage.getItem('admin_token')
      if (!token) {
        this.adminAuthed = false
        this.loadCaptcha()
        return
      }
      adminCheckAuthApi()
        .then(() => {
          this.adminAuthed = true
          if (this.isEdit) this.loadGoods()
        })
        .catch((e) => {
          console.error('管理员登录状态校验失败', e)
          window.localStorage.removeItem('admin_token')
          this.adminAuthed = false
          this.loadCaptcha()
        })
    },
    handleLogin ({ username, password, captchaCode, captchaKey }) {
      this.loginLoading = true
      adminLoginApi(username, password, captchaCode, captchaKey)
        .then((res) => {
          const token = res.data && res.data.token
          if (!token) {
            throw new Error('登录响应异常')
          }
          window.localStorage.setItem('admin_token', token)
          this.adminAuthed = true
          if (this.isEdit) this.loadGoods()
        })
        .catch((e) => {
          console.error('管理员登录失败', e)
          this.loadCaptcha()
        })
        .finally(() => {
          this.loginLoading = false
        })
    },
    // ==================== 页面跳转 ====================
    goBack () {
      // 回到后台「商品管理」并触发列表刷新
      this.$router.push({ path: '/admin', query: { tab: 'goods', refresh: String(Date.now()) } })
    },
    // ==================== 商品数据 ====================
    async loadGoods () {
      try {
        const { data } = await getAdminGoodsDetailApi(this.editId)
        const goods = data || {}
        this.form.goods_name = goods.goods_name || ''
        this.form.goods_subtitle = goods.goods_subtitle || ''
        this.form.category_id = String(goods.category_id || '')
        this.form.goods_price_min = goods.goods_price_min !== undefined ? String(goods.goods_price_min) : ''
        this.form.goods_price_max = goods.goods_price_max !== undefined ? String(goods.goods_price_max) : ''
        this.form.goods_stock = goods.goods_stock !== undefined ? String(goods.goods_stock) : '100'
        this.form.goods_image = goods.goods_image || ''
        this.form.goods_desc = goods.goods_desc || ''
        this.form.is_on_sale = Number(goods.is_on_sale) === 1
        this.form.show_on_home = Number(goods.show_on_home) === 1

        const skus = goods.skus || []
        this.form.skus = skus.map(sku => ({
          sku_id: sku.sku_id,
          spec_name: sku.spec_name || '',
          spec_values: sku.spec_values || [],
          price: sku.price !== undefined ? String(sku.price) : '',
          stock: sku.stock !== undefined ? String(sku.stock) : '',
          image: sku.image || ''
        }))

        // 回填自动发货配置（按规格 id 匹配；sku_id=0 为默认规格配置）
        const autoMap = {}
        ;(goods.auto_delivery || []).forEach(cfg => {
          autoMap[Number(cfg.sku_id)] = cfg
        })
        const defaultCfg = autoMap[0]
        this.autoDeliveryDefault = defaultCfg
          ? Object.assign(this.emptyAutoDelivery(), defaultCfg)
          : this.emptyAutoDelivery()
        this.autoDeliveryList = skus.map(sku => {
          const cfg = autoMap[Number(sku.sku_id)]
          return cfg ? Object.assign(this.emptyAutoDelivery(sku.sku_id), cfg) : this.emptyAutoDelivery(sku.sku_id)
        })
      } catch (e) {
        alert('商品信息加载失败')
        console.error(e)
      }
    },
    // ==================== 自动发货配置 ====================
    emptyAutoDelivery (skuId = 0) {
      return {
        sku_id: skuId,
        enabled: 0,
        update_order_status: 1,
        allow_repeat_card: 0,
        usage_note: '',
        card_total: 0,
        card_available: 0
      }
    },
    // 配置行名称：index = 0 为「默认规格」行
    autoRowName (index) {
      if (index === 0) {
        return (this.form.skus && this.form.skus.length)
          ? '默认规格（未单独配置的规格生效）'
          : '默认规格（无规格商品）'
      }
      const sku = this.form.skus[index - 1]
      return (sku && sku.spec_name) ? sku.spec_name : ('规格 ' + index)
    },
    // 打开卡密库管理弹窗（卡密与规格 id 绑定，需商品已保存）
    openCardModal (index) {
      const goodsId = this.goodsId || 0
      if (!goodsId) {
        alert('请先保存商品，保存后即可管理卡密库')
        return
      }
      let skuId = 0
      if (index > 0) {
        const sku = this.form.skus[index - 1]
        skuId = Number((sku && sku.sku_id) || 0)
        if (!skuId) {
          alert('该规格尚未保存，请先保存商品后再管理卡密')
          return
        }
      }
      this.cardModalGoodsId = goodsId
      this.cardModalSkuId = skuId
      this.cardModalSkuName = this.autoRowName(index)
      this.cardModalVisible = true
    },
    // 卡密库变化后刷新对应行的卡密数量
    async onCardsChanged () {
      try {
        const { data } = await getGoodsCardsApi(this.cardModalGoodsId, this.cardModalSkuId, 1, 1)
        const total = Number((data && data.total) || 0)
        const available = Number((data && data.available) || 0)
        const target = Number(this.cardModalSkuId) === 0
          ? this.autoDeliveryDefault
          : this.autoDeliveryList.find(cfg => Number(cfg.sku_id) === Number(this.cardModalSkuId))
        if (target) {
          target.card_total = total
          target.card_available = available
        }
      } catch (e) {
        // 静默失败
      }
    },
    // ==================== 规格（SKU） ====================
    addSku () {
      this.form.skus.push({
        spec_name: '',
        spec_values: [],
        price: '',
        stock: '',
        image: ''
      })
      // 自动发货配置与规格行一一对应
      this.autoDeliveryList.push(this.emptyAutoDelivery())
    },
    removeSku (index) {
      this.form.skus.splice(index, 1)
      this.autoDeliveryList.splice(index, 1)
    },
    // ==================== 图片空间 ====================
    // target: 'goods_image' 或 ('sku_image', index)
    openImagePicker (type, skuIndex) {
      this.imagePickerTarget = type === 'sku_image' ? { type, skuIndex } : { type: 'goods_image' }
      this.imagePickerVisible = true
      this.loadImageLibrary(1)
    },
    closeImagePicker () {
      this.imagePickerVisible = false
      this.imagePickerTarget = null
    },
    loadImageLibrary (page) {
      getImageLibraryApi(page || 1)
        .then((res) => {
          const data = res.data || {}
          this.imageList = data.list || []
          this.imagePage = data.page || 1
          this.imageTotalPage = data.totalPage || 1
        })
        .catch((e) => {
          console.error('图片空间加载失败', e)
        })
    },
    handlePickerUpload (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.imageUploading = true
      uploadImageLibraryApi(file)
        .then(() => {
          alert('图片上传成功')
          if (this.$refs.pickerFileInput) this.$refs.pickerFileInput.value = ''
          this.loadImageLibrary(this.imagePage)
        })
        .catch((err) => {
          console.error('图片上传失败', err)
        })
        .finally(() => {
          this.imageUploading = false
        })
    },
    selectPickedImage (img) {
      const target = this.imagePickerTarget
      if (!target) return
      if (target.type === 'sku_image') {
        const sku = this.form.skus[target.skuIndex]
        if (sku) sku.image = img.image_url
      } else {
        this.form.goods_image = img.image_url
      }
      this.closeImagePicker()
    },
    // ==================== 保存 ====================
    async submitGoods () {
      const f = this.form
      if (!f.goods_name.trim()) { alert('请输入商品名称'); return }
      if (!Number(f.category_id)) { alert('请输入正确的分类 ID'); return }
      if (!f.goods_price_min || Number(f.goods_price_min) <= 0) { alert('请输入正确的价格'); return }

      // 组装规格数据：过滤掉没有填写价格的空行
      const skus = (f.skus || [])
        .filter(sku => sku.price !== '' && Number(sku.price) > 0)
        .map(sku => ({
          sku_id: Number(sku.sku_id) || 0,
          spec_name: (sku.spec_name || '').trim(),
          spec_values: sku.spec_values || [],
          price: Number(sku.price),
          stock: Number(sku.stock) || 0,
          image: (sku.image || '').trim()
        }))

      const payload = {
        goods_name: f.goods_name.trim(),
        goods_subtitle: (f.goods_subtitle || '').trim(),
        category_id: Number(f.category_id) || 0,
        goods_price_min: Number(f.goods_price_min),
        goods_price_max: Number(f.goods_price_max) || Number(f.goods_price_min),
        goods_stock: Number(f.goods_stock) || 0,
        goods_image: (f.goods_image || '').trim(),
        goods_desc: f.goods_desc,
        is_on_sale: f.is_on_sale ? 1 : 0,
        show_on_home: f.show_on_home ? 1 : 0,
        skus
      }

      this.saving = true
      try {
        const isNew = !this.isEdit
        let goodsId = this.goodsId || 0
        if (!isNew) {
          await editGoodsApi({ ...payload, goods_id: this.editId })
        } else {
          const res = await addGoodsApi(payload)
          goodsId = Number((res.data && res.data.goodsId) || 0)
          this.isEdit = true
          this.editId = goodsId
        }
        // 保存自动发货设置（按规格绑定卡密库）
        await this.saveAutoDeliverySettings(goodsId)
        alert(isNew ? '添加成功' : '修改成功')
        this.goBack()
      } catch (e) {
        alert('保存失败')
        console.error(e)
      } finally {
        this.saving = false
      }
    },
    /**
     * 保存自动发货设置
     * 说明：规格 id 以「保存后的商品详情」为准（新增规格需先保存商品），
     *       因此按「规格名」把表单中的自动发货配置匹配到已保存的规格上。
     */
    async saveAutoDeliverySettings (goodsId) {
      if (!goodsId) return
      const pickConfig = (cfg) => ({
        enabled: Number(cfg.enabled) === 1 ? 1 : 0,
        update_order_status: Number(cfg.update_order_status) === 1 ? 1 : 0,
        allow_repeat_card: Number(cfg.allow_repeat_card) === 1 ? 1 : 0,
        usage_note: cfg.usage_note || ''
      })

      // sku_id=0：默认规格配置（无规格商品使用 / 未单独配置的规格回退使用）
      const items = [Object.assign({ sku_id: 0 }, pickConfig(this.autoDeliveryDefault))]
      try {
        const { data } = await getAdminGoodsDetailApi(goodsId)
        const savedSkus = (data && data.skus) || []
        const byName = {}
        this.autoDeliveryList.forEach((cfg, index) => {
          const sku = this.form.skus[index]
          const name = ((sku && sku.spec_name) || '').trim()
          if (!byName[name]) byName[name] = []
          byName[name].push(cfg)
        })
        savedSkus.forEach(saved => {
          const name = (saved.spec_name || '').trim()
          const queue = byName[name]
          const cfg = (queue && queue.length) ? queue.shift() : null
          if (!cfg) return
          items.push(Object.assign({ sku_id: Number(saved.sku_id) }, pickConfig(cfg)))
        })
        await saveAutoDeliveryApi(goodsId, items)
      } catch (e) {
        console.warn('自动发货设置保存失败：', e)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.goods-form-page {
  min-height: 100vh;
  background: #f5f6f8;

  // 顶部操作栏
  .form-header {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 24px;
    background: #fff;
    border-bottom: 1px solid #eef1f5;

    .header-left {
      display: flex;
      align-items: center;
      gap: 14px;
      min-width: 0;

      .back-btn {
        height: 32px;
        padding: 0 12px;
        border: 1px solid #e3e8ef;
        border-radius: 8px;
        background: #fff;
        color: #5b6673;
        font-size: 13px;
        cursor: pointer;
      }

      .page-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #1f2d3d;
      }

      .goods-id {
        font-size: 12px;
        color: #98a2b3;
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 10px;

      .ghost-btn {
        height: 34px;
        padding: 0 16px;
        border: 1px solid #e3e8ef;
        border-radius: 8px;
        background: #fff;
        color: #5b6673;
        font-size: 13px;
        cursor: pointer;
      }

      .save-btn {
        height: 34px;
        padding: 0 22px;
        border: none;
        border-radius: 8px;
        background: linear-gradient(90deg, #ecb53c, #ff9211);
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }

  // 主体：左主区 + 右侧栏
  .form-body {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 24px 40px;
    max-width: 1360px;
    margin: 0 auto;
  }

  .form-main {
    flex: 1;
    min-width: 0;
  }

  .form-side {
    width: 320px;
    flex-shrink: 0;
  }

  .card {
    background: #fff;
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);

    .card-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 15px;
      font-weight: 600;
      color: #1f2d3d;
      margin-bottom: 12px;

      .card-sub {
        font-size: 12px;
        font-weight: 400;
        color: #98a2b3;
      }
    }

    .card-tip {
      margin: 8px 0 0;
      font-size: 12px;
      color: #98a2b3;
      line-height: 18px;
    }

    &.tips ul {
      margin: 0;
      padding-left: 18px;
      font-size: 12px;
      color: #5b6673;
      line-height: 20px;
    }
  }

  .grid {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
  }

  .f {
    width: calc(50% - 7px);

    &.full {
      width: 100%;
    }

    label {
      display: block;
      font-size: 13px;
      color: #5b6673;
      margin-bottom: 6px;

      .required {
        color: #f04438;
      }
    }

    input,
    textarea {
      width: 100%;
      border: 1px solid #e3e8ef;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 13px;
      font-family: inherit;
      outline: none;
      box-sizing: border-box;

      &:focus {
        border-color: #ffb27a;
      }
    }

    textarea {
      resize: vertical;
      line-height: 20px;
    }
  }

  .empty-tip {
    padding: 16px 0;
    text-align: center;
    font-size: 13px;
    color: #98a2b3;
  }

  // 商品主图
  .image-row {
    display: flex;
    gap: 14px;
    align-items: flex-start;

    .image-preview {
      width: 120px;
      height: 120px;
      border-radius: 10px;
      border: 1px dashed #e3e8ef;
      background: #fafbfc;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .placeholder {
        font-size: 12px;
        color: #c1c7d0;
      }
    }

    .image-input {
      flex: 1;
      min-width: 0;

      input {
        width: 100%;
        border: 1px solid #e3e8ef;
        border-radius: 8px;
        padding: 8px 10px;
        font-size: 13px;
        outline: none;
        box-sizing: border-box;
      }

      .image-actions {
        display: flex;
        gap: 8px;
        margin-top: 8px;
      }
    }
  }

  .picker-btn {
    height: 32px;
    padding: 0 14px;
    border: 1px solid #6366f1;
    border-radius: 8px;
    background: #eef2ff;
    color: #6366f1;
    font-size: 13px;
    cursor: pointer;

    &.ghost {
      border-color: #e3e8ef;
      background: #fff;
      color: #98a2b3;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .inline-row {
    display: flex;
    gap: 8px;

    input { flex: 1; min-width: 0; }
  }

  // 规格行
  .sku-row {
    padding: 12px 14px;
    margin-bottom: 12px;
    border: 1px solid #f0f0f0;
    border-radius: 10px;
    background: #f9fafb;

    .sku-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;

      .sku-index {
        font-size: 13px;
        font-weight: 600;
        color: #374151;
      }

      .del-btn {
        border: none;
        background: transparent;
        color: #f04438;
        font-size: 12px;
        cursor: pointer;
      }
    }
  }

  .mini-btn {
    height: 30px;
    padding: 0 12px;
    border: 1px dashed #c7c9d4;
    border-radius: 8px;
    background: #fff;
    color: #6366f1;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      border-color: #6366f1;
      background: #eef2ff;
    }
  }

  // 自动发货
  .auto-row {
    padding: 12px 14px;
    margin-bottom: 12px;
    border: 1px solid #f0f0f0;
    border-radius: 10px;
    background: #f9fafb;

    .auto-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;

      .auto-sku {
        font-size: 13px;
        font-weight: 600;
        color: #374151;
      }

      .switch-label {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: #4b5563;
        cursor: pointer;
      }
    }

    .auto-opt {
      display: block;
      font-size: 12px;
      color: #4b5563;
      margin-bottom: 6px;
      cursor: pointer;

      input { margin-right: 6px; }
    }

    textarea {
      width: 100%;
      border: 1px solid #e3e8ef;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 12px;
      font-family: inherit;
      resize: vertical;
      outline: none;
      box-sizing: border-box;
    }

    .card-manage {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 8px;

      .card-stat {
        font-size: 12px;
        color: #6b7280;

        b { color: #f43f5e; }
      }
    }
  }

  // 侧栏开关
  .switch-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;

    .switch-name {
      font-size: 13px;
      color: #374151;
    }
  }

  .custom-switch {
    width: 46px;
    height: 26px;
    border-radius: 13px;
    background: #e5e7eb;
    position: relative;
    cursor: pointer;
    transition: background 0.2s;

    .switch-track {
      position: absolute;
      inset: 0;
      border-radius: 13px;
    }

    .switch-thumb {
      position: absolute;
      top: 3px;
      left: 3px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      transition: transform 0.2s;
    }

    &.active {
      background: #ff9211;

      .switch-thumb {
        transform: translateX(20px);
      }
    }
  }

  // ===== 图片空间选择弹窗 =====
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .modal-content {
    width: 720px;
    max-width: 92vw;
    max-height: 86vh;
    background: #fff;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid #eef1f5;
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;

    .modal-close {
      border: none;
      background: transparent;
      font-size: 16px;
      color: #98a2b3;
      cursor: pointer;
    }
  }

  .modal-body {
    padding: 16px 18px;
    overflow-y: auto;
  }

  .picker-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  .picker-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
    max-height: 420px;
    overflow-y: auto;

    .picker-card {
      border: 1px solid #eef1f5;
      border-radius: 10px;
      overflow: hidden;
      cursor: pointer;
      background: #fff;
      transition: all 0.2s;

      &:hover {
        border-color: #ff9211;
        box-shadow: 0 2px 10px rgba(255, 146, 17, 0.15);
      }

      img {
        display: block;
        width: 100%;
        height: 110px;
        object-fit: cover;
        background: #f5f6f8;
      }

      .picker-name {
        display: block;
        padding: 6px 8px;
        font-size: 12px;
        color: #5b6673;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .pager {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 14px;
    font-size: 13px;
    color: #5b6673;

    .page-btn {
      height: 30px;
      padding: 0 12px;
      border: 1px solid #e3e8ef;
      border-radius: 6px;
      background: #fff;
      font-size: 13px;
      cursor: pointer;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>
